import { chartSeriesToken, type SeriesShape } from '../ChartSeriesTokens'

/**
 * Fix for IB-90 defect 1 (Verifier S2): `chartSeriesToken(i)` already exposes
 * `.shape`/`.dash` for the >4-series rule, but `BarChart`/`DonutChart`/
 * `FunnelChart` only ever cycled colour — past 4 series two categories shared
 * an identical `var(--iw-chart-N)` fill with nothing else to tell them apart.
 * `LineChart` solves this with `strokeDasharray`; marks (bars/wedges/funnel
 * stages) have no stroke-only equivalent, so they get an SVG `<pattern>`
 * fill instead, tinted with the same ratified token colour (never a literal
 * hex) so light/dark theming still resolves via the CSS var.
 */

const PATTERN_SIZE = 8

/** Deterministic `<pattern>` id for series `index` — stable across the chart's own `<defs>` and its marks. */
export function chartPatternId(index: number): string {
  return `iw-pattern-${index}`
}

/**
 * The fill to use for series `index`: the flat ratified token for cycle 0
 * (the first 4 series — 4 distinct colours is enough), or a
 * `url(#iw-pattern-N)` reference for cycle >= 1 so a colour repeat is never
 * silent/indistinguishable (AC7 / design-system.md §5.2-5.3).
 */
export function chartFill(index: number): string {
  const token = chartSeriesToken(index)
  return token.cycle >= 1 ? `url(#${chartPatternId(index)})` : token.color
}

function patternMark(shape: SeriesShape, color: string) {
  switch (shape) {
    case 'square':
      // diagonal hatch
      return (
        <path
          d={`M0,${PATTERN_SIZE} L${PATTERN_SIZE},0`}
          stroke={color}
          strokeWidth={1.5}
          shapeRendering="crispEdges"
        />
      )
    case 'triangle':
      // dots
      return <circle cx={PATTERN_SIZE / 2} cy={PATTERN_SIZE / 2} r={1.4} fill={color} />
    case 'diamond':
      // cross-hatch
      return (
        <>
          <path d={`M0,${PATTERN_SIZE} L${PATTERN_SIZE},0`} stroke={color} strokeWidth={1.1} />
          <path d={`M0,0 L${PATTERN_SIZE},${PATTERN_SIZE}`} stroke={color} strokeWidth={1.1} />
        </>
      )
    case 'circle':
    default:
      return null
  }
}

export interface ChartPatternDefsProps {
  /** Total number of series/categories the chart is about to render marks for. */
  seriesCount: number
}

/**
 * One `<pattern>` per series whose cycle >= 1, injected as a single `<defs>`
 * block inside the chart's own SVG (Recharts passes unrecognised children
 * straight through `Surface`, so this renders as ordinary SVG). Cycle-0
 * series (the first 4) need no pattern — `chartFill()` returns the flat
 * token colour for those and this component renders nothing extra for them.
 */
export function ChartPatternDefs({ seriesCount }: ChartPatternDefsProps) {
  const patterned = Array.from({ length: seriesCount }, (_, i) => i).filter((i) => chartSeriesToken(i).cycle >= 1)
  if (patterned.length === 0) return null
  return (
    <defs>
      {patterned.map((i) => {
        const token = chartSeriesToken(i)
        return (
          <pattern
            key={chartPatternId(i)}
            id={chartPatternId(i)}
            patternUnits="userSpaceOnUse"
            width={PATTERN_SIZE}
            height={PATTERN_SIZE}
            patternTransform="rotate(45)"
          >
            <rect width={PATTERN_SIZE} height={PATTERN_SIZE} fill={token.color} fillOpacity={0.28} />
            {patternMark(token.shape, token.color)}
          </pattern>
        )
      })}
    </defs>
  )
}
